import React, { useMemo } from 'react'

import { GenericError, handleFetchMore } from '@sketch/components'
import { dataIdFromObject } from '@sketch/graphql-cache'
import { PublicationQueryPreferencesFragment } from '@sketch/gql-types'
import EmptyState from 'modules/shares/components/EmptyState'

import PublicationGrid, { PublicationGridPlaceholder } from '../PublicationGrid'
import { useCommunityModifiers, useGetCommunityPublications } from '../../hooks'
import { useTrackEvent } from '@sketch/modules-common'

const ENTRIES_PATH = ['publications', 'entries']

interface CommunityPublicationGridProps {
  analyticsId: string
  preferences: Partial<PublicationQueryPreferencesFragment> | null
}

const CommunityPublicationGrid = (props: CommunityPublicationGridProps) => {
  const { analyticsId } = props

  const { allowPagination, search, ...editorialVariables } =
    props.preferences || {}

  const { trackEvent } = useTrackEvent()
  const [{ sort, period, documentType }] = useCommunityModifiers(
    editorialVariables
  )

  const preferences = {
    ...editorialVariables,
    search,
    sort,
    period,
    documentType,
  }

  const { data, loading, error, fetchMore } = useGetCommunityPublications({
    preferences,
  })

  const isEmptySearch = search && !data?.publications.entries?.length
  const after = data?.publications.meta.after

  const handleLoadMore = useMemo(() => {
    trackEvent('COMMUNITY - Load More Items', {
      type: 'click',
    })

    return handleFetchMore(fetchMore, ENTRIES_PATH, {
      dataIdFromObject,
      after,
    })
  }, [trackEvent, fetchMore, after])

  if (loading) {
    return (
      <PublicationGridPlaceholder
        limit={props.preferences?.limit || undefined}
      />
    )
  }

  if (error || !data) {
    return <GenericError />
  }

  const publications = data.publications
  const items = publications.entries || []

  const placeholderCount = allowPagination
    ? Math.max(Math.min(publications.meta.totalCount - items.length, 20), 0)
    : 0

  return isEmptySearch ? (
    <EmptyState
      title="No search results"
      description="No document matched your search."
      icon="search"
    />
  ) : (
    <PublicationGrid
      analyticsId={analyticsId}
      items={items}
      placeholderCount={placeholderCount}
      onLoadMore={allowPagination ? handleLoadMore : undefined}
    />
  )
}

export default CommunityPublicationGrid
